import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import wsService from '../../services/websocket';
import { 
  FiArrowLeft, FiMapPin, FiPackage, FiUser, FiPhone, 
  FiClock, FiCheck, FiTruck, FiRefreshCw, FiAlertCircle,
  FiCopy, FiNavigation
} from 'react-icons/fi';
import toast from 'react-hot-toast';

const ExpressTrackingPage = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { user, token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Scroll para o topo quando a página carrega
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadOrder();
  }, [orderId, user]);

  // WebSocket para atualizações em tempo real
  useEffect(() => {
    if (!token) return;
    
    wsService.connect(token);
    
    const handleExpressUpdate = (payload) => {
      if (payload.order_id === orderId) {
        console.log('Express order update:', payload);
        loadOrder();
        
        if (payload.type === 'express_order_accepted') {
          toast.success('Um entregador aceitou seu pedido!');
        } else if (payload.type === 'express_order_status') {
          toast.success(`Status atualizado: ${getStatusLabel(payload.status)}`);
        }
      }
    };
    
    wsService.addListener('new_express_order', handleExpressUpdate);
    
    return () => {
      wsService.removeListener('new_express_order', handleExpressUpdate);
    };
  }, [token, orderId]);

  const loadOrder = async () => {
    try {
      const response = await api.get(`/express/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      toast.error('Erro ao carregar pedido');
      navigate('/cliente');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadOrder();
  };

  const copySecurityCode = () => {
    if (order?.security_code) {
      navigator.clipboard.writeText(order.security_code);
      toast.success('Código de segurança copiado!');
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Aguardando pagamento',
      'paid': 'Pago - Aguardando entregador',
      'accepted': 'Entregador a caminho da coleta',
      'picked_up': 'Item coletado - Em rota',
      'delivered': 'Entregue',
      'returning': 'Retornando ao ponto de origem',
      'returned': 'Retornado',
      'cancelled': 'Cancelado'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-gray-100 text-gray-700',
      'paid': 'bg-yellow-100 text-yellow-700',
      'accepted': 'bg-blue-100 text-blue-700',
      'picked_up': 'bg-purple-100 text-purple-700',
      'delivered': 'bg-green-100 text-green-700',
      'returning': 'bg-orange-100 text-orange-700',
      'returned': 'bg-green-100 text-green-700',
      'cancelled': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'pending': FiClock,
      'paid': FiCheck,
      'accepted': FiTruck,
      'picked_up': FiNavigation,
      'delivered': FiCheck,
      'returning': FiRefreshCw,
      'returned': FiCheck,
      'cancelled': FiAlertCircle
    };
    return icons[status] || FiClock;
  };

  const handleCancelOrder = async () => {
    if (!window.confirm('Tem certeza que deseja cancelar este pedido?')) return;
    
    try {
      await api.post(`/express/${orderId}/cancel`, { reason: 'Cancelado pelo cliente' });
      toast.success('Pedido cancelado');
      loadOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar pedido');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <FiAlertCircle className="w-16 h-16 text-red-500 mb-4" />
        <p className="text-gray-600">Pedido não encontrado</p>
        <button 
          onClick={() => navigate('/cliente')}
          className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg"
        >
          Voltar
        </button>
      </div>
    );
  }

  const StatusIcon = getStatusIcon(order.status);
  const serviceName = order.service_type === 'leva_traz' ? 'Leva e Traz' : 'Ponto a Ponto';
  const canCancel = ['pending', 'paid'].includes(order.status);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/cliente')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-bold text-gray-900">Acompanhar Pedido</h1>
              <p className="text-sm text-gray-500">{serviceName}</p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiRefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Status Card */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${getStatusColor(order.status)} mb-4`}>
            <StatusIcon className="w-5 h-5" />
            <span className="font-medium">{getStatusLabel(order.status)}</span>
          </div>
          
          {/* Timeline */}
          <div className="space-y-3 mt-4">
            {[
              { key: 'created', label: 'Pedido criado', time: order.created_at, done: true },
              { key: 'paid', label: 'Pagamento confirmado', time: order.payment_status === 'paid' ? order.created_at : null, done: order.payment_status === 'paid' },
              { key: 'accepted', label: 'Entregador aceitou', time: order.accepted_at, done: !!order.accepted_at },
              { key: 'picked_up', label: 'Item coletado', time: order.picked_up_at, done: !!order.picked_up_at },
              { key: 'delivered', label: 'Entregue ao destinatário', time: order.delivered_at, done: !!order.delivered_at },
              ...(order.return_trip ? [
                { key: 'returned', label: 'Retornado ao remetente', time: order.returned_at, done: !!order.returned_at }
              ] : [])
            ].map((step, idx) => (
              <div key={step.key} className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                  step.done ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'
                }`}>
                  {step.done ? <FiCheck className="w-4 h-4" /> : <span className="text-xs">{idx + 1}</span>}
                </div>
                <div className="flex-1">
                  <p className={`text-sm ${step.done ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                    {step.label}
                  </p>
                  {step.time && (
                    <p className="text-xs text-gray-500">
                      {new Date(step.time).toLocaleString('pt-BR')}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Security Code - Show when accepted */}
        {order.security_code && ['accepted', 'picked_up', 'returning'].includes(order.status) && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-800 font-medium mb-1">Código de Segurança</p>
                <p className="text-3xl font-bold text-yellow-900 font-mono">{order.security_code}</p>
                <p className="text-xs text-yellow-700 mt-1">Informe ao entregador para confirmar a entrega</p>
              </div>
              <button
                onClick={copySecurityCode}
                className="p-3 bg-yellow-200 rounded-lg hover:bg-yellow-300 transition-colors"
              >
                <FiCopy className="w-5 h-5 text-yellow-800" />
              </button>
            </div>
          </div>
        )}

        {/* Driver Info */}
        {order.driver_id && (
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FiTruck className="w-5 h-5 text-red-600" />
              Entregador
            </h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">{order.driver_name || 'Entregador'}</p>
                {order.driver_phone && (
                  <p className="text-sm text-gray-500">{order.driver_phone}</p>
                )}
              </div>
              {order.driver_phone && (
                <a
                  href={`tel:${order.driver_phone}`}
                  className="p-3 bg-green-100 rounded-full hover:bg-green-200 transition-colors"
                >
                  <FiPhone className="w-5 h-5 text-green-600" />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Addresses */}
        <div className="bg-white rounded-xl p-4 shadow-sm space-y-4">
          {/* Pickup */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <FiMapPin className="w-4 h-4 text-green-600" />
              </div>
              <span className="font-medium text-gray-900">Coleta</span>
            </div>
            <p className="text-gray-700 ml-10">{order.pickup_address}</p>
            {order.pickup_complement && (
              <p className="text-sm text-gray-500 ml-10">{order.pickup_complement}</p>
            )}
          </div>

          {/* Arrow */}
          <div className="ml-4 flex flex-col items-center">
            <div className="w-px h-4 bg-gray-300"></div>
            <FiNavigation className="w-4 h-4 text-gray-400 rotate-90" />
            <div className="w-px h-4 bg-gray-300"></div>
          </div>

          {/* Delivery */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <FiMapPin className="w-4 h-4 text-red-600" />
              </div>
              <span className="font-medium text-gray-900">Entrega</span>
            </div>
            <p className="text-gray-700 ml-10">{order.delivery_address}</p>
            {order.delivery_complement && (
              <p className="text-sm text-gray-500 ml-10">{order.delivery_complement}</p>
            )}
          </div>

          {/* Return if applicable */}
          {order.return_trip && (
            <>
              <div className="ml-4 flex flex-col items-center">
                <div className="w-px h-4 bg-gray-300"></div>
                <FiRefreshCw className="w-4 h-4 text-gray-400" />
                <div className="w-px h-4 bg-gray-300"></div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <FiMapPin className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="font-medium text-gray-900">Retorno</span>
                </div>
                <p className="text-gray-700 ml-10">{order.pickup_address}</p>
              </div>
            </>
          )}
        </div>

        {/* Recipient */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <FiUser className="w-5 h-5 text-blue-600" />
            Destinatário
          </h3>
          <div className="space-y-2">
            <p className="text-gray-700">{order.recipient_name}</p>
            <p className="text-gray-500 text-sm">{order.recipient_phone}</p>
          </div>
        </div>

        {/* Item Description */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <FiPackage className="w-5 h-5 text-purple-600" />
            Item Transportado
          </h3>
          <p className="text-gray-700">{order.item_description}</p>
          {order.notes && (
            <p className="text-sm text-gray-500 mt-2 italic">{order.notes}</p>
          )}
        </div>

        {/* Payment Summary */}
        <div className="bg-gray-900 text-white rounded-xl p-4">
          <h3 className="font-semibold mb-3">Resumo do Pagamento</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-300">
              <span>Taxa de coleta</span>
              <span>R$ {order.pickup_fee?.toFixed(2)}</span>
            </div>
            {order.return_trip && (
              <div className="flex justify-between text-gray-300">
                <span>Taxa de retorno</span>
                <span>R$ {order.delivery_fee?.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-gray-700 font-semibold text-lg">
              <span>Total</span>
              <span className="text-yellow-400">R$ {order.total?.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Cancel Button */}
        {canCancel && (
          <button
            onClick={handleCancelOrder}
            className="w-full py-3 border border-red-300 text-red-600 rounded-xl font-medium hover:bg-red-50 transition-colors"
          >
            Cancelar Pedido
          </button>
        )}
      </main>
    </div>
  );
};

export default ExpressTrackingPage;
